import { useEffect, useState } from "react";
import { MapPin } from "lucide-react";
import { QuoteButton } from "@/components/QuoteButton";
import { MenuButton } from "@/components/MenuButton";
import { HalalSection } from "@/components/HalalSection";
import { useContent, useSiteImage } from "@/context/ContentContext";
import { useReveal, Seo } from "@/lib/seo";

const OCCASIONS = [
  "Dîner romantique en couple",
  "Anniversaire ou occasion spéciale",
  "Dîner entre amis à domicile",
  "Repas privé dans une ambiance chaleureuse",
  "Dîner privé premium ou VIP",
  "Projet sur mesure selon le lieu et les besoins",
];

const CHEF = "Cheffe à domicile";

function HeroSlider({ slides }) {
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setTimeout(() => setI((v) => (v + 1) % slides.length), 5500);
    return () => clearTimeout(t);
  }, [i, slides.length]);
  return (
    <section className="relative flex h-[100svh] min-h-[600px] items-center justify-center overflow-hidden" data-testid="cheffe-hero">
      {slides.map((s, k) => (
        <div key={k} aria-hidden={k !== i} className={`absolute inset-0 transition-opacity duration-[1400ms] ease-out ${k === i ? "opacity-100" : "opacity-0"}`} data-testid={`chef-slide-${k}`}>
          <img src={s.src} alt={s.alt} className={`h-full w-full object-cover transition-transform duration-[7000ms] ease-out ${k === i ? "scale-110" : "scale-100"}`} />
        </div>
      ))}
      <div className="absolute inset-0 bg-[#2C2C2C]/55" />
      <div className="absolute inset-0 bg-gradient-to-b from-[#2C2C2C]/40 via-transparent to-[#2C2C2C]" />
      <div className="relative z-10 mx-auto max-w-4xl px-5 text-center fade-up">
        <p className="mb-6 text-sm uppercase tracking-[0.4em] text-[#C9A84C]">Cheffe à domicile</p>
        <h1 className="font-serif text-5xl font-400 italic leading-[1.02] text-[#FAF6EF] sm:text-6xl lg:text-8xl">Une table d'exception, chez vous</h1>
        <p className="mx-auto mt-7 max-w-xl text-base leading-relaxed text-white/80 lg:text-lg" key={i} data-testid="chef-slide-caption">{slides[i].caption}</p>
        <div className="mt-10 flex flex-col items-center gap-4">
          <QuoteButton testId="chef-hero-quote" service={CHEF} variant="gold" className="px-10 py-4 text-base">Organiser mon dîner privé</QuoteButton>
          <MenuButton variant="light" testId="chef-hero-carte">Découvrir notre carte</MenuButton>
        </div>
      </div>
      <div className="absolute bottom-10 left-1/2 z-10 flex -translate-x-1/2 gap-3" data-testid="chef-slider-dots">
        {slides.map((s, k) => (
          <button key={k} onClick={() => setI(k)} aria-label={s.alt} data-testid={`chef-slide-dot-${k}`} className={`h-1.5 rounded-full transition-[width,background-color] duration-500 ${k === i ? "w-10 bg-[#C9A84C]" : "w-4 bg-white/50 hover:bg-white"}`} />
        ))}
      </div>
    </section>
  );
}

export default function CheffeADomicile() {
  useReveal();
  const { content } = useContent();
  const img = useSiteImage();
  const zone = content?.zones?.chef;
  const slides = [
    { src: img("chef_slide_1"), alt: "Dîner romantique aux chandelles pour deux", caption: "Un dîner aux chandelles, rien que pour vous deux." },
    { src: img("chef_slide_2"), alt: "Dîner convivial entre amis à la maison", caption: "Des rires, des plats à partager, et vos amis autour de la table." },
    { src: img("chef_slide_3"), alt: "Repas de famille servi comme au restaurant", caption: "Le restaurant s'invite à la maison, pour toute la famille." },
  ];

  return (
    <div data-testid="cheffe-page" className="bg-[#2C2C2C] text-white">
      <Seo
        title="Cheffe à domicile halal — cuisine africaine, antillaise, française & afro-caribéenne — Afro Regal"
        description="Cheffe à domicile halal : dîner romantique, repas entre amis ou en famille, dîner privé VIP. Cuisine africaine halal, antillaise halal, française halal et afro-caribéenne halal chez vous. Option non halal sur demande. Sur devis."
      />

      <HeroSlider slides={slides} />

      <section className="py-20 text-center lg:py-24">
        <p className="reveal mx-auto max-w-2xl px-5 text-lg leading-relaxed text-white/75 lg:text-xl">
          Une expérience culinaire personnalisée dans le confort de votre domicile ou d'un lieu privé, pensée pour les couples, les particuliers et les projets premium ou VIP — avec une cuisine <strong className="text-[#C9A84C]">africaine, antillaise, française ou afro-caribéenne halal</strong>.
        </p>
      </section>

      <HalalSection variant="dark" title="Une cheffe à domicile halal" intro="Votre dîner privé est cuisiné en halal, notre offre principale : saveurs d'Afrique, des Antilles, de France ou un métissage afro-caribéen, servis chez vous." testId="cheffe-halal" />

      <section className="border-y border-white/10 py-24 lg:py-32">
        <div className="mx-auto max-w-5xl px-5 lg:px-8">
          <p className="reveal text-sm uppercase tracking-[0.3em] text-[#C9A84C]">Positionnement</p>
          <h2 className="reveal mt-5 font-serif text-4xl leading-tight text-[#FAF6EF] lg:text-6xl">« L'intimité d'un grand restaurant, l'élégance en plus »</h2>
          <div className="mt-12 grid gap-10 lg:grid-cols-12">
            <div className="reveal mx-auto w-full max-w-sm overflow-hidden rounded-t-[200px] rounded-b-2xl border border-[#C9A84C]/40 p-2 lg:col-span-5">
              <img src={img("chef_hero")} alt="Cheffe dressant une assiette pour un dîner privé" className="aspect-[3/4] w-full rounded-t-[192px] rounded-b-xl object-cover" />
            </div>
            <div className="reveal flex flex-col justify-center lg:col-span-7">
              <p className="text-lg leading-relaxed text-white/75">
                Nous imaginons un moment entièrement dédié à vos envies : un menu composé pour vous, un service discret et soigné, et toute l'attention portée aux détails qui font la différence. Vous profitez pleinement de vos invités — nous nous occupons du reste.
              </p>
              <div className="mt-9 flex flex-wrap gap-4">
                <QuoteButton testId="chef-cta-quote" service={CHEF} variant="gold">Organiser mon dîner privé</QuoteButton>
                <MenuButton variant="light" testId="chef-carte">Découvrir notre carte</MenuButton>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 lg:py-32">
        <div className="mx-auto grid max-w-6xl gap-14 px-5 lg:grid-cols-12 lg:px-8">
          <div className="hidden lg:col-span-4 lg:block">
            <div className="sticky top-28 overflow-hidden rounded-2xl">
              <img src={img("chef_section")} alt="Table élégante dressée pour un dîner privé" className="aspect-[3/4] w-full object-cover" />
            </div>
          </div>
          <div className="lg:col-span-8">
          <p className="reveal text-sm uppercase tracking-[0.3em] text-[#C9A84C]">Les occasions</p>
          <h2 className="reveal mt-4 font-serif text-4xl text-[#FAF6EF] lg:text-5xl">Pour chaque moment qui compte</h2>
          <ol className="mt-12">
            {OCCASIONS.map((o, i) => (
              <li key={o} className="reveal group flex items-center gap-6 border-b border-white/10 py-6 transition-colors hover:border-[#C9A84C]/60" style={{ transitionDelay: `${i * 50}ms` }} data-testid={`occasion-${i}`}>
                <span className="font-serif text-2xl italic text-[#C9A84C]">{String(i + 1).padStart(2, "0")}</span>
                <span className="font-serif text-2xl text-white/90 transition-transform duration-300 group-hover:translate-x-2 lg:text-3xl">{o}</span>
              </li>
            ))}
          </ol>
          <div className="reveal mt-14 flex flex-col items-center gap-6 text-center">
            <p className="flex max-w-xl items-start gap-3 text-sm text-white/70"><MapPin size={18} className="mt-0.5 shrink-0 text-[#C9A84C]" /> {zone}</p>
            <p className="text-sm text-white/50">Prestations sur devis — chaque expérience est personnalisée.</p>
            <QuoteButton testId="chef-final-quote" service={CHEF} variant="gold">Organiser mon dîner privé</QuoteButton>
          </div>
          </div>
        </div>
      </section>
    </div>
  );
}
